import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Autorenew, CheckCircleOutline } from "@mui/icons-material";
import axios from "../../../api/axios";
import {
  PROFITABILITY_RECALCULATE_TIMEOUT,
  SETTLEMENTS_PROFITABILITY_RECALCULATE_ENDPOINT,
} from "../../../consts";
import { ProfitabilityRecalculation } from "../../../types/profitability";
import { formatNumber } from "../../../utils";
import { PROFITABILITY_RECALCULATED_EVENT } from "./profitabilityMonth";

// What a recalculation reads but never writes, and what it replaces with
// today's data - see recalculate_month_profitability (backend).
const KEPT = [
  "Revenue - the settlement amount, calculated or overridden",
  "Printed pages - from the readings the settlement was confirmed with",
  "Prices of the services on jobs - recorded when each service was added",
  "Invoices, statuses, numbers and sale dates",
  "Settlements awaiting confirmation - they get profitability when confirmed",
];
const REPLACED = [
  "Page costs - the equipment's own, or the default",
  "Mean job execution cost",
  "Which jobs count - jobs finished or backdated into the period since are added, deleted ones drop out",
  "Used parts - at their batch's current price",
  "The stored cost breakdown",
];

const ChangeList = ({ title, icon, items }: { title: string; icon: ReactNode; items: string[] }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem key={item} disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>{icon}</ListItemIcon>
            <ListItemText primary={t(item)} primaryTypographyProps={{ variant: "body2" }} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

/**
 * A recalculation covers every confirmed settlement of the month, the tab only
 * those that already have profitability - says so when the two differ (e.g. a
 * migrated month), so the count doesn't read as a mismatch with the cards.
 */
const ShownOnTabNote = ({ settlements, shown }: { settlements: number; shown: number }) => {
  const { t } = useTranslation();
  if (shown >= settlements) {
    return null;
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {t(
        "This tab counts {{shown}} of them - the others have no profitability yet (migrated, or confirmed before it was introduced); recalculating computes it.",
        { shown }
      )}
    </Typography>
  );
};

/** What the recalculation did - only known once it ran: previewing the
 * differences would cost a whole recalculation of its own. */
const RecalculationResult = ({ result }: { result: ProfitabilityRecalculation }) => {
  const { t } = useTranslation();
  return (
    <Alert severity="success">
      <Typography variant="body2" fontWeight="bold">
        {t("Settlements recalculated: {{count}}", { count: result.settlements })}
      </Typography>
      <ShownOnTabNote settlements={result.settlements} shown={result.shown} />
      <Typography variant="body2">
        {t("Profitability changed: {{count}}", { count: result.changed })}
      </Typography>
      {result.changed > 0 && (
        <Typography variant="body2">
          {t("Net profitability of the month on this tab: {{before}} → {{after}}", {
            before: formatNumber(result.net_before),
            after: formatNumber(result.net_after),
          })}
        </Typography>
      )}
      {result.failed > 0 && (
        <Typography variant="body2" color="error">
          {t("Could not be recalculated and stay as they are: {{count}}", { count: result.failed })}
        </Typography>
      )}
    </Alert>
  );
};

interface RecalculateProfitabilityProps {
  /** YYYY-MM - the month the Profitability tab shows. */
  month: string;
  onDone: () => void;
}

/**
 * Confirms recalculating a month's profitability: how many settlements it
 * covers, what stays and what is replaced; then applies it and reports what
 * changed.
 */
export const RecalculateProfitability = ({ month, onDone }: RecalculateProfitabilityProps) => {
  const { t } = useTranslation();
  const [scope, setScope] = useState<{ settlements: number; shown: number } | null>(null);
  const [applying, setApplying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [result, setResult] = useState<ProfitabilityRecalculation | null>(null);
  const monthLabel = dayjs(`${month}-01`).format("MM.YYYY");

  useEffect(() => {
    let cancelled = false;
    setScope(null);
    axios
      .get(SETTLEMENTS_PROFITABILITY_RECALCULATE_ENDPOINT, { params: { month } })
      .then((response) => {
        if (!cancelled) setScope(response.data as { settlements: number; shown: number });
      })
      .catch(() => {
        // Only the count is missing - the recalculation itself is still offered.
      });
    return () => {
      cancelled = true;
    };
  }, [month]);

  const handleConfirm = async () => {
    setApplying(true);
    setFailed(false);
    try {
      const { data } = await axios.post(
        SETTLEMENTS_PROFITABILITY_RECALCULATE_ENDPOINT,
        { month },
        { timeout: PROFITABILITY_RECALCULATE_TIMEOUT }
      );
      setResult(data as ProfitabilityRecalculation);
      window.dispatchEvent(new Event(PROFITABILITY_RECALCULATED_EVENT));
    } catch {
      setFailed(true);
    } finally {
      setApplying(false);
    }
  };

  if (result) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <RecalculationResult result={result} />
        <Button variant="contained" onClick={onDone}>
          {t("Close")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Profitability of the confirmed settlements with a sale date in {{month}} is computed again from today's costs, jobs and part prices.",
          { month: monthLabel }
        )}
      </Typography>

      {scope?.settlements === 0 ? (
        <Alert severity="info">{t("No confirmed settlements in this month.")}</Alert>
      ) : (
        scope && (
          <Alert severity="info" icon={false}>
            <Typography variant="body2" fontWeight="bold">
              {t("Settlements to recalculate: {{count}}", { count: scope.settlements })}
            </Typography>
            <ShownOnTabNote settlements={scope.settlements} shown={scope.shown} />
          </Alert>
        )
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <ChangeList
          title={t("Stays as it is")}
          icon={<CheckCircleOutline fontSize="small" color="success" />}
          items={KEPT}
        />
        <ChangeList
          title={t("Replaced with today's data")}
          icon={<Autorenew fontSize="small" color="warning" />}
          items={REPLACED}
        />
      </Box>

      <Alert severity="warning">
        {t(
          "The figures stored so far are not kept anywhere - a recalculation cannot be undone."
        )}
      </Alert>

      {failed && <Alert severity="error">{t("Failed to recalculate profitability")}</Alert>}
      {applying && <LinearProgress />}

      <Button
        variant="contained"
        color="warning"
        onClick={handleConfirm}
        disabled={applying || scope?.settlements === 0}
      >
        {applying ? t("Recalculating...") : t("Recalculate {{month}}", { month: monthLabel })}
      </Button>
    </Box>
  );
};
